import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

import Confetti from "./Confetti";

interface ProjectFormProps {
  onClose?: () => void;
}

export default function ProjectForm({ onClose }: ProjectFormProps) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    projectDescription: "",
    expectedStartDate: "",
    budgetRange: ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Validate that the date format is correct (YYYY-MM-DD)
    if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return; // Don't update if format is invalid
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // const handleDateClick = (e: React.MouseEvent<HTMLInputElement>) => {
  //   e.currentTarget.focus();
  //   e.currentTarget.click();
  // };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.projectDescription || !formData.expectedStartDate || !formData.budgetRange) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch("/api/project-submissions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Track successful form submission
        // Analytics tracking removed - only GTM remains active
        
        // Trigger confetti celebration
        setShowConfetti(true);
        
        toast({
          title: "Success!",
          description: "Your project information has been submitted. We'll get back to you within 24 hours.",
        });
        
        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          projectDescription: "",
          expectedStartDate: "",
          budgetRange: ""
        });
        
        // Close form after confetti finishes
        setTimeout(() => {
          if (onClose) {
            onClose();
          }
        }, 3000);
      } else {
        throw new Error(result.error || "Submission failed");
      }
      
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Confetti 
        active={showConfetti} 
        onComplete={() => setShowConfetti(false)} 
      />
      <div className="max-w-2xl mx-auto p-6">
        <Card className="bg-charcoal/80 backdrop-blur-md border-cyber-purple/30">
        <CardHeader className="text-center pb-6">
          {/* Cyberpunk Unicorn Logo */}
          <div className="mx-auto mb-6 w-32 h-32 bg-gradient-to-br from-cyber-purple via-neon-pink to-cyber-blue rounded-2xl flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-cyber-purple/20 via-neon-pink/20 to-cyber-blue/20 animate-pulse"></div>
            <div className="relative text-6xl">🦄</div>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">General Information</h2>
          <p className="text-gray-400">Tell us about your project requirements</p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* First Name */}
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-white font-medium">
                First Name <span className="text-neon-pink">*</span>
              </Label>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleInputChange}
                className="bg-dark-slate/50 border-gray-600 text-gray-700 placeholder-gray-400 focus:border-cyber-purple focus:ring-cyber-purple"
                required
              />
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-white font-medium">
                Last Name <span className="text-neon-pink">*</span>
              </Label>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleInputChange}
                className="bg-dark-slate/50 border-gray-600 text-gray-700 placeholder-gray-400 focus:border-cyber-purple focus:ring-cyber-purple"
                required
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white font-medium">
                Email <span className="text-neon-pink">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
                className="bg-dark-slate/50 border-gray-600 text-gray-700 placeholder-gray-400 focus:border-cyber-purple focus:ring-cyber-purple"
                required
              />
            </div>

            {/* Project Description */}
            <div className="space-y-2">
              <Label htmlFor="projectDescription" className="text-white font-medium">
                Provide a brief description of your project and its objectives. <span className="text-neon-pink">*</span>
              </Label>
              <Textarea
                id="projectDescription"
                name="projectDescription"
                placeholder="Describe your project requirements, timeline, and objectives..."
                value={formData.projectDescription}
                onChange={handleInputChange}
                className="bg-dark-slate/50 border-gray-600 text-gray-700 placeholder-gray-400 focus:border-cyber-purple focus:ring-cyber-purple min-h-[120px] resize-none"
                required
              />
            </div>

            {/* Expected Start Date */}
            <div className="space-y-2">
              <Label htmlFor="expectedStartDate" className="text-white font-medium">
                Expected Start Date <span className="text-neon-pink">*</span>
              </Label>
              <Input
                id="expectedStartDate"
                name="expectedStartDate"
                type="date"
                value={formData.expectedStartDate}
                onChange={handleDateInputChange}
                min="2024-01-01"
                max="2030-12-31"
                className="bg-dark-slate/50 border-gray-600 text-gray-700 placeholder-gray-400 focus:border-cyber-purple focus:ring-cyber-purple"
                required
              />
            </div>

            {/* Budget Range */}
            <div className="space-y-2">
              <Label htmlFor="budgetRange" className="text-white font-medium">
                Budget Range <span className="text-neon-pink">*</span>
              </Label>
              <Select value={formData.budgetRange} onValueChange={(value) => handleSelectChange("budgetRange", value)}>
                <SelectTrigger className="bg-dark-slate/50 border-gray-600 text-white placeholder:text-white focus:border-cyber-purple focus:ring-cyber-purple [&>span]:text-white">
                  <SelectValue placeholder="Select your budget range" className="text-white placeholder:text-white" />
                </SelectTrigger>
                <SelectContent className="bg-dark-slate border-gray-600">
                  <SelectItem value="under-5k" className="text-white hover:bg-cyber-purple/20">Under $5,000</SelectItem>
                  <SelectItem value="5k-15k" className="text-white hover:bg-cyber-purple/20">$5,000 - $15,000</SelectItem>
                  <SelectItem value="15k-30k" className="text-white hover:bg-cyber-purple/20">$15,000 - $30,000</SelectItem>
                  <SelectItem value="30k-50k" className="text-white hover:bg-cyber-purple/20">$30,000 - $50,000</SelectItem>
                  <SelectItem value="50k-100k" className="text-white hover:bg-cyber-purple/20">$50,000 - $100,000</SelectItem>
                  <SelectItem value="over-100k" className="text-white hover:bg-cyber-purple/20">Over $100,000</SelectItem>
                  <SelectItem value="to-discuss" className="text-white hover:bg-cyber-purple/20">To be discussed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-cyber-gradient hover:bg-gradient-to-r hover:from-neon-pink hover:to-cyber-purple text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                {isSubmitting ? "Submitting..." : "Submit Project Details"}
              </Button>
            </div>
          </form>
        </CardContent>
        </Card>
      </div>
    </>
  );
}